import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, TONE, type ToneName } from './primitives';
import { useBelow } from '../lib/useMediaQuery';

// The feedback layer (DS-16, T5). There were two copy-pasted, error-only toasts
// that vanished after 4.5s beneath the dialogs, 35 local message states, and no
// live region anywhere: success was silent, failure was silent or green, and a
// failed read looked like an empty list (TS-14). Every write reports success and
// failure; a failed read renders LoadError, never an empty state.

export type StatusTone = 'success' | 'info' | 'warning' | 'error';
type Action = { label: string; onClick: () => void };

// info is neutral: accent means selection, never "look at this" (UI-CONVENTIONS §1).
const STATUS_TONE: Record<StatusTone, ToneName> = {
  success: 'success',
  info: 'neutral',
  warning: 'warning',
  error: 'danger',
};

// ── InlineStatus ──────────────────────────────────────────────────────────────
// A message beside the thing it is about. Also the only feedback inside a modal
// dialog: everything outside an open <dialog> is inert, the Toast host included.
// Same `action` shape as a toast, so one moves to the other without a rewrite.
export const InlineStatus = ({
  tone,
  children,
  action,
}: {
  tone: StatusTone;
  children: ReactNode;
  action?: Action;
}) => {
  const t = TONE[STATUS_TONE[tone]];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--hf-space-3)',
        padding: 'var(--hf-space-2) var(--hf-space-3)',
        borderRadius: 'var(--hf-radius-md)',
        background: t.bg,
        border: `1px solid ${t.bd}`,
        color: t.fg,
        fontSize: 'var(--hf-text-sm)',
        lineHeight: 'var(--hf-leading-snug)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {action && (
        <Button variant="ghost" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
};

// ── LoadError ─────────────────────────────────────────────────────────────────
// What a reader renders when its query errors. `data ?? []` turned a network or
// RLS failure into "No time logged for this week" (TS-14, WS-V1, ORG-V3).
export const LoadError = ({ what, onRetry }: { what: string; onRetry: () => void }) => (
  <InlineStatus tone="error" action={{ label: 'Retry', onClick: onRetry }}>
    Couldn’t load {what}.
  </InlineStatus>
);

// ── EmptyState ────────────────────────────────────────────────────────────────
// "Nothing yet" — a real, successful, empty read — with the next thing to do.
// ~19 bespoke "No … yet" strings, each padded differently (DS-18): vertical
// padding only, so it sits flush with the card's own content edge.
export const EmptyState = ({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  /** The next step, usually a Button or a Link. */
  action?: ReactNode;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 'var(--hf-space-2)',
      padding: 'var(--hf-space-4) 0',
    }}
  >
    <p
      style={{
        margin: 0,
        fontSize: 'var(--hf-text-md)',
        fontWeight: 'var(--hf-weight-medium)',
        color: 'var(--hf-ink-2)',
      }}
    >
      {title}
    </p>
    {children && (
      <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>{children}</div>
    )}
    {action}
  </div>
);

// ── Toast ─────────────────────────────────────────────────────────────────────
// Page-level feedback, one host for the app. success/info are polite and leave
// after TOAST_MS (paused while hovered or focused, WCAG 2.2.1); an error is an
// alert and stays until dismissed — the old toasts took a failure away after
// 4.5s, before anyone had read it (XA-V3).
//
// The host is a popover="manual" in the top layer, so no z-index fight with the
// dialogs (it used to sit at 200, under them at 500). It is re-shown on every
// push because the top layer stacks in the order things were shown. It is NOT
// for feedback about an action inside a modal dialog: the dialog makes the rest
// of the page inert, this host included, so that goes in an InlineStatus in the
// dialog.
//
// Safari before 17 and Firefox before 125 have no Popover API. There the host
// is a plain fixed box above the nav and under the hand-rolled modals (z 500),
// where the old toasts sat. Calling showPopover() unchecked threw at mount and
// took every route down to the root error boundary.
//
// The two regions are always present while the host is: a live region that
// appears together with its first message is often not announced.
export type ToastInput = {
  tone: 'success' | 'info' | 'error';
  message: ReactNode;
  /** e.g. Undo. Runs, then dismisses the toast. */
  action?: Action;
};
type ToastItem = ToastInput & { id: number };

export const TOAST_MS = 4500;

// Outside a provider push does nothing, so a screen's unit test needn't mount
// the host. App.tsx mounts it around the shell.
const ToastContext = createContext<{ push: (t: ToastInput) => void }>({ push: () => {} });

// A hook beside its provider is the pattern; this file has no state for Fast
// Refresh to lose that the rule would be protecting.
// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => useContext(ToastContext);

const canPop = (el: HTMLElement | null): el is HTMLElement => typeof el?.showPopover === 'function';

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const host = useRef<HTMLDivElement>(null);
  // A phone: the stack spans the width, and sits above the day-clock bar
  // (--hf-actionbar-h, set in tokens.css while the bar is on screen) rather than
  // over the Check in it holds.
  const phone = useBelow('md');

  useEffect(() => {
    const el = host.current;
    if (!canPop(el)) return;
    el.showPopover();
    return () => el.hidePopover();
  }, []);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((t: ToastInput) => {
    const el = host.current;
    // Not over an open modal: everything outside it is inert, so a toast shown
    // on top could be neither clicked nor announced, and would cover the
    // dialog's footer. It waits underneath and is there when the dialog closes.
    if (canPop(el) && !document.querySelector('dialog:modal')) {
      el.hidePopover();
      el.showPopover();
    }
    const id = ++nextId.current;
    setItems((xs) => [...xs, { ...t, id }]);
  }, []);
  // One object for the provider's life: a consumer that lists it in an effect's
  // deps would otherwise re-run (and could re-push) on every toast change.
  const value = useMemo(() => ({ push }), [push]);

  const card = (x: ToastItem) => <ToastCard key={x.id} item={x} dismiss={dismiss} />;
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        ref={host}
        popover="manual"
        // The UA popover box is centred, bordered and painted Canvas; this is a
        // transparent, bottom-right stack that lets clicks through between toasts.
        // position and zIndex are the no-Popover fallback; the top layer
        // ignores zIndex and is already fixed.
        style={{
          position: 'fixed',
          zIndex: 'calc(var(--hf-z-nav) + 1)',
          // A phone's edges clear the home indicator and, in landscape, the
          // notch (viewport-fit=cover); the clock bar's height already does.
          inset: phone
            ? [
                'auto',
                'max(var(--hf-space-4), env(safe-area-inset-right))',
                'max(calc(var(--hf-space-4) + var(--hf-actionbar-h, 0px)), env(safe-area-inset-bottom))',
                'max(var(--hf-space-4), env(safe-area-inset-left))',
              ].join(' ')
            : 'auto var(--hf-space-5) var(--hf-space-5) auto',
          margin: 0,
          padding: 0,
          border: 0,
          background: 'transparent',
          overflow: 'visible',
          maxWidth: phone ? 'none' : 'min(26rem, calc(100vw - 2 * var(--hf-space-5)))',
          pointerEvents: 'none',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
          <div role="status" style={stack}>
            {items.filter((x) => x.tone !== 'error').map(card)}
          </div>
          <div role="alert" style={stack}>
            {items.filter((x) => x.tone === 'error').map(card)}
          </div>
        </div>
      </div>
    </ToastContext.Provider>
  );
};

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' } as const;

const ToastCard = ({ item, dismiss }: { item: ToastItem; dismiss: (id: number) => void }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cameFrom = useRef<HTMLElement | null>(null);
  const { id, tone, message, action } = item;
  const lingers = tone === 'error' || hovered || focused;
  useEffect(() => {
    if (lingers) return;
    // ponytail: a pause restarts the full TOAST_MS rather than resuming the
    // remainder; track the elapsed time if anyone ever notices the difference.
    const t = setTimeout(() => dismiss(id), TOAST_MS);
    return () => clearTimeout(t);
  }, [lingers, id, dismiss]);

  // Dismiss and Undo unmount the button that has focus, which dropped it to
  // <body> and sent the next Tab back to the top of the page (WCAG 2.4.3). Hand
  // it back to where it came from, else to the next toast's Dismiss.
  const close = () => {
    const card = ref.current;
    if (card?.contains(document.activeElement)) {
      const others = [
        ...(card.closest('[popover]') ?? document).querySelectorAll<HTMLElement>('[data-toast]'),
      ].filter((c) => c !== card);
      const next =
        others.find((c) => card.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_FOLLOWING) ??
        others.at(-1);
      const to = cameFrom.current?.isConnected
        ? cameFrom.current
        : next?.querySelector<HTMLElement>('button:last-of-type');
      to?.focus();
    }
    dismiss(id);
  };

  // Tinted like InlineStatus, over the opaque paper so the page doesn't show
  // through: a 3px coloured edge alone was the side-stripe the audit flags, and
  // told success from error by hue and nothing else.
  const t = TONE[STATUS_TONE[tone]];
  return (
    <div
      ref={ref}
      data-toast=""
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={(e) => {
        setFocused(true);
        const from = e.relatedTarget;
        if (!e.currentTarget.contains(from)) {
          cameFrom.current = from instanceof HTMLElement && from !== document.body ? from : null;
        }
      }}
      onBlur={() => setFocused(false)}
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--hf-space-2)',
        padding: 'var(--hf-space-2) var(--hf-space-2) var(--hf-space-2) var(--hf-space-3)',
        borderRadius: 'var(--hf-radius-md)',
        background: `linear-gradient(${t.bg}, ${t.bg}) var(--hf-paper-2)`,
        border: `1px solid ${t.bd}`,
        boxShadow: 'var(--hf-shadow-pop)',
        color: t.fg,
        fontSize: 'var(--hf-text-sm)',
        lineHeight: 'var(--hf-leading-snug)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      {action && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            action.onClick();
            close();
          }}
        >
          {action.label}
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon="x"
        onClick={close}
        style={{ padding: 'var(--hf-space-2)' }}
      >
        <span className="hf-sr-only">Dismiss</span>
      </Button>
    </div>
  );
};
